import React from 'react';
import PersonMetrics from './PersonMetrics';
import Scorecard from './Scorecard';
import { dealMix, carriedIn, initials, pct, revenueOf } from './metrics';
import { toneFor } from './personTone';
import MeetingDealTable from '../MeetingDealTable';
import ClosedDeals from './ClosedDeals';
import InsightQuestion, { INSIGHT_CATEGORIES } from './InsightQuestion';
import './PersonSection.css';

/**
 * One person's card.
 *
 * Two separate ideas, deliberately kept apart:
 *   `canEdit`  — this reader is PERMITTED to change this section (an admin, or the
 *                salesperson themself). Decided in MeetingDetail; when it is false
 *                there is no Edit button at all and the card reads as plain text.
 *   `editable` — the card is UNLOCKED right now, i.e. permitted AND the reader has
 *                pressed Edit. This is the only flag handed to the children, so the
 *                page is read-only until somebody says otherwise and a stray click
 *                can never change a figure.
 *
 * Saving is unchanged: each field still saves as you leave it, so Done only locks
 * the card again — there is nothing held back to commit or discard.
 */
export default function PersonSection({ sec, meetingId, month, meetingDate, teamYtdTotal,
                                        editable, canEdit, onToggleEdit,
                                        onSaveInsightItems, onSaveScorecard, onDealsChange }) {
  const mix = dealMix(sec.deals);
  const carried = carriedIn(sec);
  // Set once here as a CSS custom property; every descendant (avatar, mix
  // bar, metric progress bars) reads it back via `var(--tone, ...)` rather
  // than each recomputing it — same mechanism the approved mockup uses.
  const tone = toneFor(sec);
  const performance = pct(revenueOf(sec), sec?.checkins?.total?.floor);
  const performanceLabel = performance == null
    ? 'No monthly target'
    : performance > 100
      ? `${Math.round(performance)}% · above target`
      : Math.round(performance) === 100
        ? '100% · on target'
        : `${Math.round(performance)}% · needs focus`;
  const bookingDerived = sec?.checkins?.total?.source === 'booking';
  const namedPerformance = !bookingDerived ? performanceLabel
    : sec?.checkins?.total?.floor == null ? 'No delivered target'
      : Number(sec.checkins.total.floor) === 0 ? 'Delivered target RM 0'
        : performance == null ? 'Delivered not yet recorded'
          : `Delivered ${performanceLabel}`;
  const performanceKind = performance == null ? 'neutral' : performance >= 100 ? 'good' : 'risk';

  return (
    <section className={'ml-person' + (editable ? ' is-editing' : '')}
             id={`person-${sec.salesperson_id}`} style={{ '--tone': tone }}>
      <div className="ml-person-head">
        <div className="ml-person-identity">
          <span className="ml-person-av" data-testid="person-initials">{initials(sec.salesperson_name)}</span>
          <div>
            <span className="ml-person-kicker">Salesperson note</span>
            <h3 className="ml-person-name">{sec.salesperson_name}</h3>
          </div>
        </div>
        <div className="ml-person-head-actions">
          <span className={`ml-person-status is-${performanceKind}`}>{namedPerformance}</span>
          {editable && <span className="ml-person-editing" data-testid="editing-tag">editing</span>}
          <div className="ml-mix">
            <span className="ml-mix-bar">
              <i style={{ width: `${mix.total ? mix.won / mix.total * 100 : 0}%`, background: 'var(--rmc-green)' }} />
              <i style={{ width: `${mix.total ? mix.open / mix.total * 100 : 0}%`, background: 'var(--tone, var(--rmc-blue))' }} />
              <i style={{ width: `${mix.total ? mix.lost / mix.total * 100 : 0}%`, background: 'var(--rmc-coral)' }} />
            </span>
            <span className="ml-mix-legend">
              <span data-testid="mix-won">{mix.won} won</span>
              <span data-testid="mix-open">{mix.open} open</span>
              <span data-testid="mix-lost">{mix.lost} lost</span>
            </span>
          </div>
          {canEdit && (
            <button type="button"
                    className={'md-btn ml-person-edit' + (editable ? ' primary' : '')}
                    onClick={onToggleEdit}
                    aria-label={`${editable ? 'Done editing' : 'Edit'} ${sec.salesperson_name}'s section`}>
              {editable ? 'Done' : 'Edit'}
            </button>
          )}
        </div>
      </div>

      <PersonMetrics sec={sec} meetingDate={meetingDate || month} teamYtdTotal={teamYtdTotal} />

      {/* Renders nothing when the API sent no figures — a meeting frozen before the
          scorecard existed has none, and zeros would be a claim, not a record. It also
          renders nothing for a month of pure catering work (`hidden`), where every
          event-space figure would be a zero that reads as failure. The metrics, deals
          and insights around it are unaffected either way. */}
      <Scorecard sec={sec} month={month} editable={editable}
                 onSave={onSaveScorecard ? (next) => onSaveScorecard(sec, next) : undefined} />

      <div className="ml-sub">
        <span className="ml-eyebrow">APO — High Ticket Sales Focus</span>
        {carried > 0 && (
          <span className="ml-carried" data-testid="carried-badge">{carried} carried forward</span>
        )}
      </div>
      <MeetingDealTable meetingId={meetingId} salespersonId={sec.salesperson_id}
                        deals={sec.deals} editable={editable} onChange={onDealsChange} />
      {/* Won and lost deals leave the table the week after they close (the carry
          leaves both behind); this is where they are found for the month. Handed
          the week's deals so it refetches when one is marked closed just now. */}
      <ClosedDeals meetingId={meetingId} salespersonId={sec.salesperson_id} month={month}
                   editable={editable} deals={sec.deals} onReopened={onDealsChange} />

      <div className="ml-sub"><span className="ml-eyebrow">Insights</span></div>
      <div className="md-qa-grid">
        {INSIGHT_CATEGORIES.map(c => (
          <InsightQuestion key={c.category} category={c.category} items={sec[c.itemsField]}
                           editable={editable}
                           onSave={(next) => onSaveInsightItems(sec, c.category, next)} />
        ))}
      </div>
    </section>
  );
}
